/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme, type ResolvedTheme, type ThemePreference } from '@vaas/react-ui-kit';

import { useUserId } from '@/api/hooks';
import { getUserSettings, saveUserSettings, type UserSettingsModel } from '@/api/settings';

/**
 * Single write path for theme changes: applies it through the kit's theme provider and persists it
 * to the user-settings API. Buffers early choices until settings arrive.
 *
 * The provider owns what is painted and what is cached locally; this hook only adds the half the
 * kit cannot know about, which is that this app's preference is stored server-side per user.
 * Reading the current theme does not need this hook - use the kit's `useTheme` directly.
 */
export function useThemeToggle(): {
  resolved: ResolvedTheme;
  set: (next: ThemePreference) => void;
  toggle: () => void;
} {
  const { resolvedTheme, setTheme } = useTheme();
  const gmin = useUserId();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ['user-settings', gmin],
    queryFn: () => getUserSettings(gmin),
    enabled: Boolean(gmin),
    retry: false,
  });

  // Buffer theme choice until settings load so it can be persisted with the full payload.
  const pendingTheme = useRef<ThemePreference | null>(null);

  const set = useCallback(
    (next: ThemePreference) => {
      setTheme(next);
      if (!gmin) return;

      const key = ['user-settings', gmin] as const;
      // Read the latest cached settings before merging the theme update.
      const current = queryClient.getQueryData<UserSettingsModel>(key);
      if (!current) {
        // Settings not loaded yet: buffer the choice and reconcile it once they arrive.
        pendingTheme.current = next;
        return;
      }
      pendingTheme.current = null;

      // Merge onto cached settings so concurrent updates survive.
      queryClient.setQueryData<UserSettingsModel>(key, (prev) => ({
        ...(prev as UserSettingsModel),
        themeSetting: next,
      }));

      void saveUserSettings(gmin, {
        language: current.languageDisplay.languageCode,
        theme: next,
      }).catch(() => {
        // Best-effort persistence; the in-session theme already applied.
      });
    },
    [setTheme, gmin, queryClient],
  );

  // Persist any theme buffered while settings were loading.
  useEffect(() => {
    if (!settingsQuery.data || !pendingTheme.current) return;
    const next = pendingTheme.current;
    pendingTheme.current = null;
    set(next);
  }, [settingsQuery.data, set]);

  const toggle = useCallback(
    () => set(resolvedTheme === 'dark' ? 'light' : 'dark'),
    [resolvedTheme, set],
  );

  return { resolved: resolvedTheme, set, toggle };
}
